import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LanguageContext";
import { COUNTRIES, DEFAULT_COUNTRY } from "@/lib/countries";
import { Loader2, MapPin, ShieldCheck, User, ChevronLeft, ChevronDown, AlertTriangle } from "lucide-react";

const fmtErr = (e) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x.msg || JSON.stringify(x)).join(", ");
  return e?.message || "Something went wrong";
};

const getLocation = () =>
  new Promise((resolve, reject) => {
    if (!("geolocation" in navigator)) return reject(new Error("no-geo"));
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      (err) => reject(err),
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 60000 }
    );
  });

/**
 * Phone-OTP customer signup panel.
 * Full name + phone + terms/location → SMS OTP (WebOTP auto-fill) → account.
 * Props: onSuccess(user), onLoginClick(), compact (bool)
 */
export const CustomerSignup = ({ onSuccess, onLoginClick, compact = false }) => {
  const { setSession } = useAuth();
  const { lang } = useLang();
  const isAr = lang === "ar";

  const [step, setStep] = useState("form"); // form | otp
  const [fullName, setFullName] = useState("");
  const [dialCode, setDialCode] = useState(DEFAULT_COUNTRY.dial);
  const [phone, setPhone] = useState("");
  const [sentPhone, setSentPhone] = useState("");
  const [terms, setTerms] = useState(false);
  const [otp, setOtp] = useState("");
  const [devCode, setDevCode] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [locBlocked, setLocBlocked] = useState(false);
  const abortRef = useRef(null);

  const T = {
    title: isAr ? "أنشئ حسابك" : "Create your account",
    sub: isAr ? "سجّل بالاسم ورقم الهاتف فقط" : "Sign up with just your name and phone",
    name: isAr ? "الاسم الكامل" : "Full name",
    phone: isAr ? "رقم الهاتف" : "Phone number",
    send: isAr ? "أرسل رمز التحقق" : "Send verification code",
    terms1: isAr ? "أوافق على " : "I agree to the ",
    termsLink: isAr ? "الشروط والأحكام" : "Terms & Conditions",
    terms2: isAr
      ? " وأمنح التطبيق حق الوصول إلى موقعي لتحديد ولايتي وتقديم الخدمات."
      : " and grant location access so we can detect my state and serve you.",
    otpTitle: isAr ? "أدخل رمز التحقق" : "Enter verification code",
    otpSub: isAr ? "أرسلنا رمزًا مكونًا من 6 أرقام إلى" : "We sent a 6-digit code to",
    verify: isAr ? "تحقق وتابع" : "Verify & continue",
    resend: isAr ? "إعادة الإرسال" : "Resend code",
    back: isAr ? "رجوع" : "Back",
    haveAcc: isAr ? "لديك حساب؟" : "Already have an account?",
    login: isAr ? "تسجيل الدخول" : "Log in",
    locating: isAr ? "جارٍ تحديد موقعك…" : "Detecting your location…",
  };

  const startWebOtp = () => {
    if (!("OTPCredential" in window)) return null;
    const controller = new AbortController();
    abortRef.current = controller;
    return navigator.credentials
      .get({ otp: { transport: ["sms"] }, signal: controller.signal })
      .then((cred) => cred?.code || null)
      .catch(() => null);
  };

  const handleSend = async (e) => {
    e?.preventDefault?.();
    setErr(""); setLocBlocked(false);
    if (fullName.trim().length < 2) return setErr(isAr ? "أدخل اسمك الكامل" : "Please enter your full name.");
    if (phone.replace(/\D/g, "").length < 6) return setErr(isAr ? "أدخل رقم هاتف صحيح" : "Please enter a valid phone number.");
    if (!terms) return setErr(isAr ? "يجب الموافقة على الشروط والوصول للموقع" : "You must accept the Terms and location access.");

    setLoading(true);
    // Get location first (required)
    let loc;
    try {
      loc = await getLocation();
    } catch (_e) {
      setLoading(false);
      setLocBlocked(true);
      return setErr(isAr
        ? "الوصول إلى الموقع مطلوب. يرجى تفعيل خدمة الموقع والسماح بالوصول ثم المحاولة مرة أخرى."
        : "Location access is required. Please enable location, allow access, and try again.");
    }

    // Start WebOTP listener BEFORE the SMS is sent (Android Chrome)
    const otpPromise = startWebOtp();

    const localNum = phone.replace(/\D/g, "").replace(/^0+/, "");
    const fullPhone = `${dialCode}${localNum}`;
    setSentPhone(fullPhone);

    try {
      const { data } = await api.post("/auth/phone/send-otp", {
        full_name: fullName.trim(),
        phone: fullPhone,
        terms_accepted: true,
        lat: loc.lat,
        lng: loc.lng,
      });
      setStep("otp");
      if (data.dev_code) setDevCode(data.dev_code);
      if (otpPromise) {
        otpPromise.then((code) => {
          if (code) { setOtp(code); handleVerify(code); }
        });
      }
    } catch (e2) {
      abortRef.current?.abort?.();
      setErr(fmtErr(e2));
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (codeArg) => {
    const code = (codeArg ?? otp).toString().replace(/\D/g, "");
    if (code.length !== 6) return setErr(isAr ? "أدخل الرمز المكون من 6 أرقام" : "Enter the 6-digit code.");
    setLoading(true); setErr("");
    try {
      const { data } = await api.post("/auth/phone/verify-otp", { phone: sentPhone, code });
      setSession(data.token, data.user);
      onSuccess?.(data.user);
    } catch (e) {
      setErr(fmtErr(e));
    } finally {
      setLoading(false);
    }
  };

  if (step === "otp") {
    return (
      <div data-testid="customer-otp-step">
        <button type="button" onClick={() => { setStep("form"); setOtp(""); setErr(""); }}
          className="mb-3 inline-flex items-center gap-1 text-sm text-[var(--so-text-soft)] hover:text-[var(--so-orange)]"
          data-testid="otp-back">
          <ChevronLeft className="w-4 h-4" /> {T.back}
        </button>
        <h2 className="text-2xl font-bold">{T.otpTitle}</h2>
        <p className="text-sm text-[var(--so-text-soft)] mt-1">{T.otpSub} <span dir="ltr" className="font-semibold">{sentPhone}</span></p>

        {devCode && (
          <div className="mt-3 text-xs rounded-lg p-2.5 bg-amber-500/10 border border-amber-500/30 text-amber-700 dark:text-amber-400" data-testid="dev-code-hint">
            {isAr ? "وضع تجريبي (بدون SMS بعد): رمزك هو" : "TEST MODE (no SMS yet) — your code is"} <b className="tracking-widest">{devCode}</b>
          </div>
        )}

        <form onSubmit={(e) => { e.preventDefault(); handleVerify(); }} className="mt-5">
          <input
            autoFocus
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            placeholder="••••••"
            value={otp}
            onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
            className="so-input text-center text-2xl tracking-[0.5em] font-bold"
            dir="ltr"
            data-testid="otp-input"
          />
          {err && <div className="mt-3 text-sm text-red-500 bg-red-500/10 border border-red-500/20 rounded-lg p-3" data-testid="otp-error">{err}</div>}
          <button type="submit" disabled={loading}
            className="w-full mt-4 inline-flex items-center justify-center gap-2 rounded-xl px-4 py-3 font-bold text-white disabled:opacity-60 bg-[var(--so-orange)] hover:brightness-110 transition-all"
            data-testid="otp-verify-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />} {T.verify}
          </button>
          <button type="button" onClick={handleSend} disabled={loading}
            className="w-full mt-2 text-sm text-[var(--so-text-soft)] hover:text-[var(--so-orange)]" data-testid="otp-resend-btn">
            {T.resend}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div data-testid="customer-signup-form">
      {!compact && <h2 className="text-2xl font-bold">{T.title}</h2>}
      <p className="text-sm text-[var(--so-text-soft)] mt-1">{T.sub}</p>

      <form onSubmit={handleSend} className="mt-5 space-y-3">
        <div className="relative">
          <User className="absolute top-1/2 -translate-y-1/2 start-3.5 w-4 h-4 text-[var(--so-text-soft)] pointer-events-none z-10" />
          <input className="so-input" style={{ paddingInlineStart: "2.75rem" }} placeholder={T.name} value={fullName}
            onChange={(e) => setFullName(e.target.value)} required data-testid="signup-name" />
        </div>

        <div className="flex gap-2" dir="ltr">
          <div className="relative flex-shrink-0" style={{ width: "7.5rem" }}>
            <select
              value={dialCode}
              onChange={(e) => setDialCode(e.target.value)}
              className="so-input appearance-none cursor-pointer w-full"
              style={{ width: "100%", paddingInlineStart: "0.7rem", paddingInlineEnd: "1.6rem" }}
              data-testid="signup-country-code"
            >
              {COUNTRIES.map((c) => (
                <option key={c.iso} value={c.dial}>{c.flag} {c.dial}</option>
              ))}
            </select>
            <ChevronDown className="w-4 h-4 absolute top-1/2 -translate-y-1/2 end-2 pointer-events-none text-[var(--so-text-soft)]" />
          </div>
          <input className="so-input flex-1 min-w-0" style={{ width: "auto" }} type="tel" dir="ltr" placeholder="9xx xxx xxx" value={phone}
            onChange={(e) => setPhone(e.target.value)} required data-testid="signup-phone" />
        </div>

        <label className="flex items-start gap-2.5 cursor-pointer select-none pt-1" data-testid="signup-terms-label">
          <input type="checkbox" checked={terms} onChange={(e) => setTerms(e.target.checked)}
            className="mt-0.5 w-4 h-4 accent-[var(--so-orange)] flex-shrink-0" data-testid="signup-terms" />
          <span className="text-xs text-[var(--so-text-soft)] leading-relaxed">
            {T.terms1}
            <Link to="/legal/terms" target="_blank" className="text-[var(--so-orange)] font-semibold underline">{T.termsLink}</Link>
            {T.terms2}
          </span>
        </label>

        <div className="flex items-center gap-1.5 text-[11px] text-[var(--so-text-soft)]">
          <MapPin className="w-3.5 h-3.5 text-[var(--so-orange)]" />
          {isAr ? "نستخدم موقعك لتأكيد توفر الخدمة في ولايتك." : "We use your location to confirm service is available in your state."}
        </div>

        {err && (
          <div className={`text-sm rounded-lg p-3 border flex items-start gap-2 ${locBlocked ? "text-amber-700 dark:text-amber-400 bg-amber-500/10 border-amber-500/30" : "text-red-500 bg-red-500/10 border-red-500/20"}`} data-testid="signup-error">
            {locBlocked && <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />}
            <span>{err}</span>
          </div>
        )}

        <button type="submit" disabled={loading}
          className="w-full inline-flex items-center justify-center gap-2 rounded-xl px-4 py-3.5 font-bold text-white disabled:opacity-60 bg-[var(--so-orange)] hover:brightness-110 transition-all"
          data-testid="signup-send-otp-btn">
          {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> {T.locating}</> : T.send}
        </button>
      </form>

      <div className="mt-5 text-sm text-center text-[var(--so-text-soft)]">
        {T.haveAcc}{" "}
        {onLoginClick ? (
          <button type="button" onClick={onLoginClick} className="text-[var(--so-orange)] font-semibold" data-testid="signup-login-link">{T.login}</button>
        ) : (
          <Link to="/login" className="text-[var(--so-orange)] font-semibold" data-testid="signup-login-link">{T.login}</Link>
        )}
      </div>
    </div>
  );
};
